import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, Phone } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/constants";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

// Minimal markdown-ish renderer for headings, bold, lists, paragraphs
function renderContent(content) {
  const lines = content.split("\n");
  const blocks = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      blocks.push(<ul key={`ul-${key}`} className="list-disc pl-6 space-y-2 my-4 text-lg text-foreground/80">{list.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(li) }} />)}</ul>);
      list = [];
    }
  };
  const inline = (t) => t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flush(idx); return; }
    if (line.startsWith("## ")) { flush(idx); blocks.push(<h2 key={idx} className="font-heading text-3xl text-primary mt-8 mb-2">{line.slice(3)}</h2>); }
    else if (/^[-*]\s|^\d+\.\s/.test(line)) { list.push(line.replace(/^[-*]\s|^\d+\.\s/, "")); }
    else { flush(idx); blocks.push(<p key={idx} className="text-lg text-foreground/80 leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
  });
  flush("end");
  return blocks;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSEO({ title: post ? `${post.title} | Esprit Home Healthcare` : "Article | Esprit Home Healthcare", description: post?.excerpt });

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="container-page py-32 text-center">
        <h1 className="font-heading text-4xl text-primary">Article not found</h1>
        <Link to="/blog" className="inline-flex items-center gap-2 text-primary font-semibold mt-4"><ArrowLeft className="w-5 h-5" /> Back to Blog</Link>
      </div>
    );

  if (!post) return <div className="container-page py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <article>
      {post.cover_image && (
        <div className="h-[320px] lg:h-[420px] w-full overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="container-page max-w-3xl py-12 lg:py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-primary font-semibold mb-6"><ArrowLeft className="w-5 h-5" /> All Articles</Link>
        <div className="flex items-center gap-2 text-muted-foreground"><CalendarDays className="w-4 h-4" /> {fmtDate(post.created_at)} · {post.author}</div>
        <h1 className="font-heading text-4xl md:text-5xl text-primary mt-3 leading-tight">{post.title}</h1>
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {post.tags.map((t) => <span key={t} className="rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground">{t}</span>)}
          </div>
        )}
        <div className="mt-8">{renderContent(post.content)}</div>

        <div className="bg-primary text-primary-foreground rounded-2xl p-8 mt-12 text-center">
          <h3 className="font-heading text-3xl">Ready to start care at home?</h3>
          <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 px-7 text-lg font-bold mt-5 transition-colors">
            <Phone className="w-5 h-5" /> Call {COMPANY.phone}
          </a>
        </div>
      </div>
    </article>
  );
}
